import { useState, useMemo, useEffect } from "react";
import HelpButton from '../../tours/HelpButton';
import useAutoTour from '../../tours/useAutoTour';
import { EXPENSE_CATEGORIES } from "../../services/expenseService";
import { useExpenses } from "../../contexts/ExpensesContext";
import { useCurrentUser } from "../../contexts/CurrentUserContext";
import { api } from "../../api/client";
import { getProjects } from "../../services/projectService";
import { useContacts } from "../../contexts/ContactsContext";
import ExpenseCard from "./ExpenseCard";
import ExpenseForm from "./ExpenseForm";

function fmtMoney(n) {
  return `£${Number(n).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}


const btnPrimary = {
  padding: "0.55rem 1.2rem", background: "#E4E69B", border: "none",
  borderRadius: 7, fontWeight: 700, fontSize: "0.9rem", cursor: "pointer",
  fontFamily: "Raleway, sans-serif", color: "#0C2D48",
};

const inputStyle = {
  padding: "0.5rem 0.7rem", borderRadius: 7, border: "1px solid #ddd",
  fontSize: "0.88rem", fontFamily: "Raleway, sans-serif",
  background: "#fff", color: "#333",
};

export default function ExpenseList() {
  useAutoTour('expenses');
  const { expenses, setExpenses, refresh } = useExpenses();
  const { currentUser } = useCurrentUser();
  // TeamBooks P2-3: backend-computed truth (owner or finance) — matches the
  // /api/expenses/:id/approve gate, so admins don't see buttons that 403.
  const canApprove = !!currentUser?.canApproveExpenses;
  const [showForm, setShowForm] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [toast, setToast] = useState(null);

  // Filters
  const [search, setSearch] = useState("");
  const [catFilter, setCatFilter] = useState("");
  const [projectFilter, setProjectFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [billableFilter, setBillableFilter] = useState("all");

  // Date column sort
  const [sortDir, setSortDir] = useState("desc");
  const [allProjects, setAllProjects] = useState([]);

  const { contacts: allContacts } = useContacts();
  // Build lookup maps for project/contact names
  const projectsLookup = Object.fromEntries(allProjects.map(p => [p.id, p.name]));
  const contactsLookup = Object.fromEntries(allContacts.map(c => [c.id, c.name]));

  useEffect(() => {
    let alive = true;
    getProjects()
      .then((items) => {
        if (alive) setAllProjects(Array.isArray(items) ? items : []);
      })
      .catch(() => {
        if (alive) setAllProjects([]);
      });

    return () => {
      alive = false;
    };
  }, []);

  function showToast(msg) {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  }

  // ─── CRUD ───────────────────────────────────────────────────────────────────
  async function handleSave(data) {
    // Map snake_case form fields → camelCase API fields
    const payload = {
      title: data.title,
      amount: data.amount,
      date: data.date,
      category: data.category,
      projectId: data.project_id ?? null,
      contactId: data.contact_id ?? null,
      receiptUrl: data.receipt_url ?? null,
      notes: data.notes ?? '',
      isBillable: data.is_billable ?? false,
      isBilled: data.is_billed ?? false,
      taxRate: data.tax_rate ?? 0,
    };

    if (data.id) {
      // Edit — optimistic update then API PUT
      setExpenses(prev => prev.map(e => e.id === data.id ? { ...e, ...data } : e));
      showToast("Expense updated.");
      api(`/api/expenses/${data.id}`, { method: 'PUT', body: JSON.stringify(payload) })
        .catch(err => { console.error('Failed to update expense:', err); refresh(); });
    } else {
      // Create — optimistic prepend then API POST
      const tempId = Date.now();
      const optimistic = {
        title: '', amount: 0, date: new Date().toISOString().slice(0, 10),
        category: '', project_id: null, contact_id: null, receipt_url: null,
        notes: '', is_billable: false, is_billed: false, tax_rate: 0,
        created_at: new Date().toISOString(),
        ...data,
        id: tempId,
      };
      setExpenses(prev => [optimistic, ...prev]);
      showToast("Expense saved.");
      api('/api/expenses', { method: 'POST', body: JSON.stringify(payload) })
        .then(res => res.json())
        .then(d => {
          if (d.expense) {
            setExpenses(prev => prev.map(e => e.id === tempId ? { ...optimistic, id: Number(d.expense.id) } : e));
          }
        })
        .catch(err => { console.error('Failed to create expense:', err); refresh(); });
    }
    setShowForm(false);
    setEditTarget(null);
  }

  async function handleDelete(id) {
    if (!window.confirm("Delete this expense? This cannot be undone.")) return;
    setExpenses(prev => prev.filter(e => e.id !== id));
    showToast("Expense deleted.");
    api(`/api/expenses/${id}`, { method: 'DELETE' })
      .catch(err => { console.error('Failed to delete expense:', err); refresh(); });
  }

  function handleEdit(expense) {
    setEditTarget(expense);
    setShowForm(true);
  }

  // ─── Approval (owner/finance only) ────────────────────────────────────────────
  async function handleApprove(id) {
    setExpenses(prev => prev.map(e => e.id === id ? { ...e, approval_status: 'approved', rejection_reason: null } : e));
    showToast("Expense approved.");
    api(`/api/expenses/${id}/approve`, { method: 'PUT' })
      .catch(err => { console.error('Failed to approve expense:', err); refresh(); });
  }

  async function handleReject(id) {
    const reason = window.prompt('Reason for rejection (optional):');
    if (reason === null) return; // cancelled
    setExpenses(prev => prev.map(e => e.id === id ? { ...e, approval_status: 'rejected', rejection_reason: reason || null } : e));
    showToast("Expense rejected.");
    api(`/api/expenses/${id}/reject`, { method: 'PUT', body: JSON.stringify({ reason }) })
      .catch(err => { console.error('Failed to reject expense:', err); refresh(); });
  }

  // ─── Filtered + sorted list ─────────────────────────────────────────────────
  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    let list = expenses.filter(e => {
      const matchSearch = !q || e.title?.toLowerCase().includes(q);
      const matchCat = !catFilter || e.category === catFilter;
      const matchProject = !projectFilter
        ? true
        : projectFilter === "none" ? !e.project_id : String(e.project_id) === projectFilter;
      const matchDateFrom = !dateFrom || e.date >= dateFrom;
      const matchDateTo = !dateTo || e.date <= dateTo;
      const matchBillable = billableFilter === "all"
        ? true
        : billableFilter === "billable" ? e.is_billable : !e.is_billable;
      return matchSearch && matchCat && matchProject && matchDateFrom && matchDateTo && matchBillable;
    });

    list.sort((a, b) => {
      const d = a.date.localeCompare(b.date);
      return sortDir === "asc" ? d : -d;
    });

    return list;
  }, [expenses, search, catFilter, projectFilter, dateFrom, dateTo, billableFilter, sortDir]);

  // ─── Summary ────────────────────────────────────────────────────────────────
  const summary = useMemo(() => {
    const total = filtered.reduce((s, e) => s + Number(e.amount), 0);
    const billable = filtered.filter(e => e.is_billable).reduce((s, e) => s + Number(e.amount), 0);
    const nonBillable = filtered.filter(e => !e.is_billable).reduce((s, e) => s + Number(e.amount), 0);
    return { total, billable, nonBillable };
  }, [filtered]);

  const hasActiveFilter = search || catFilter || projectFilter || dateFrom || dateTo || billableFilter !== "all";

  // ─── Render ─────────────────────────────────────────────────────────────────
  return (
    <div style={{ padding: "1rem", maxWidth: 1100, margin: "0 auto" }}>

      {/* Page header */}
      <div style={{
        display: "flex", justifyContent: "space-between", alignItems: "center",
        marginBottom: "1.25rem", flexWrap: "wrap", gap: "0.75rem",
      }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <h2 style={{ margin: 0, fontSize: "1.4rem", fontFamily: "Poppins, sans-serif" }}>
              Expenses
            </h2>
            <HelpButton tourId="expenses" />
          </div>
          <div style={{ fontSize: "0.82rem", color: "#aaa", marginTop: "0.15rem" }}>
            {expenses.length} expense{expenses.length !== 1 ? "s" : ""}
          </div>
        </div>
        <button data-tour-id="expenses-new" onClick={() => { setEditTarget(null); setShowForm(true); }} style={btnPrimary}>
          + New expense
        </button>
      </div>

      {/* Summary bar */}
      <div data-tour-id="expenses-summary" style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap", marginBottom: "1.25rem" }}>
        {[
          {
            label: "Total expenses",
            value: fmtMoney(summary.total),
            sub: `${filtered.length} shown`,
            color: "#0C2D48",
          },
          {
            label: "Billable",
            value: fmtMoney(summary.billable),
            sub: `${filtered.filter(e => e.is_billable).length} expenses`,
            color: "#7a4a10",
          },
          {
            label: "Non-billable",
            value: fmtMoney(summary.nonBillable),
            sub: `${filtered.filter(e => !e.is_billable).length} expenses`,
            color: "#555",
          },
        ].map(({ label, value, sub, color }) => (
          <div key={label} style={{
            flex: 1, minWidth: 130, padding: "0.9rem 1rem",
            background: "#fff", border: "1px solid #35454C",
            borderRadius: 10, boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
          }}>
            <div style={{ fontSize: "0.72rem", fontWeight: 700, color: "#aaa", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "0.3rem" }}>
              {label}
            </div>
            <div style={{ fontSize: "1.35rem", fontWeight: 900, color }}>{value}</div>
            <div style={{ fontSize: "0.75rem", color: "#aaa", marginTop: "0.15rem" }}>{sub}</div>
          </div>
        ))}
      </div>

      {/* Filter bar */}
      <div data-tour-id="expenses-filter" style={{
        display: "flex", flexWrap: "wrap", gap: "0.6rem",
        marginBottom: "1rem", padding: "0.85rem 1rem",
        background: "#fff", border: "1px solid #e8e8e8", borderRadius: 10,
      }}>
        {/* Search */}
        <input
          type="text"
          placeholder="Search by title…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ ...inputStyle, flex: "1 1 180px", minWidth: 160 }}
        />

        {/* Category */}
        <select value={catFilter} onChange={e => setCatFilter(e.target.value)} style={{ ...inputStyle, minWidth: 180 }}>
          <option value="">All categories</option>
          {EXPENSE_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>

        {/* Project */}
        {allProjects.length > 0 && (
          <select value={projectFilter} onChange={e => setProjectFilter(e.target.value)} style={{ ...inputStyle, minWidth: 150 }}>
            <option value="">All projects</option>
            <option value="none">— Unattached —</option>
            {allProjects.map(p => <option key={p.id} value={String(p.id)}>{p.name}</option>)}
          </select>
        )}

        {/* Date from */}
        <input
          type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
          title="From date" style={{ ...inputStyle, minWidth: 130 }}
        />

        {/* Date to */}
        <input
          type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
          title="To date" style={{ ...inputStyle, minWidth: 130 }}
        />

        {/* Billable */}
        <select value={billableFilter} onChange={e => setBillableFilter(e.target.value)} style={{ ...inputStyle, minWidth: 150 }}>
          <option value="all">All expenses</option>
          <option value="billable">Billable only</option>
          <option value="non-billable">Non-billable only</option>
        </select>

        {/* Clear */}
        {hasActiveFilter && (
          <button
            onClick={() => {
              setSearch(""); setCatFilter(""); setProjectFilter("");
              setDateFrom(""); setDateTo(""); setBillableFilter("all");
            }}
            style={{
              padding: "0.5rem 0.75rem", background: "none", border: "none",
              color: "#aaa", cursor: "pointer", fontSize: "0.82rem",
              fontFamily: "Raleway, sans-serif",
            }}
          >
            Clear all ✕
          </button>
        )}
      </div>

      {/* Table */}
      {filtered.length === 0 ? (
        <p style={{ textAlign: "center", color: "#aaa", padding: "2.5rem 0", fontSize: "0.9rem" }}>
          {expenses.length === 0
            ? "No expenses yet. Add your first one."
            : "No expenses match your filters."}
        </p>
      ) : (
        <div data-tour-id="expenses-list" className="expense-table-wrapper" style={{ overflowX: "auto", borderRadius: 10, border: "1px solid #35454C", background: "#fff" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 640 }}>
            <thead>
              <tr style={{ background: "#0C2D48", color: "#fff" }}>
                <th
                  onClick={() => setSortDir(d => d === "asc" ? "desc" : "asc")}
                  style={{
                    padding: "0.65rem 0.75rem", textAlign: "left", fontWeight: 700,
                    fontSize: "0.8rem", cursor: "pointer", userSelect: "none",
                    whiteSpace: "nowrap",
                  }}
                >
                  Date {sortDir === "asc" ? "↑" : "↓"}
                </th>
                {["Title", "Category", "Project", "Supplier", "Amount", "Billable", "Status", "Receipt", "Actions"].map(h => (
                  <th
                    key={h}
                    style={{
                      padding: "0.65rem 0.75rem", fontWeight: 700, fontSize: "0.8rem",
                      textAlign: h === "Amount" ? "right" : h === "Billable" || h === "Receipt" ? "center" : "left",
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(expense => (
                <ExpenseCard
                  key={expense.id}
                  expense={expense}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onApprove={handleApprove}
                  onReject={handleReject}
                  canApprove={canApprove}
                  projectsLookup={projectsLookup}
                  contactsLookup={contactsLookup}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Form modal */}
      {showForm && (
        <ExpenseForm
          expense={editTarget}
          onSave={handleSave}
          onClose={() => { setShowForm(false); setEditTarget(null); }}
        />
      )}

      {/* Toast */}
      {toast && (
        <div style={{
          position: "fixed", bottom: "1.5rem", left: "50%",
          transform: "translateX(-50%)", background: "#0C2D48",
          color: "#fff", padding: "0.7rem 1.4rem", borderRadius: 8,
          fontWeight: 600, fontSize: "0.9rem", zIndex: 2000,
          boxShadow: "0 4px 16px rgba(0,0,0,0.2)", whiteSpace: "nowrap",
        }}>
          ✓ {toast}
        </div>
      )}
    </div>
  );
}
